import { useState, useCallback } from 'react';

const BASE_URL = '/api';

/**
 * Tagasta valitud sait-i juhtimispath. Kui SiteSelector on slot valinud,
 * suuname `/sites/:slot/control/*`-le; muidu vana `/control/*` (main slot).
 */
function _ctrlPath(action) {
  try {
    const v = localStorage.getItem('rohepaneel:selectedSlot') ?? localStorage.getItem('lynxpower:selectedSlot');
    if (v) return `/sites/${parseInt(v)}/control/${action}`;
  } catch (_) { /* localStorage unavailable */ }
  return `/control/${action}`;
}

async function fetchApi(endpoint, options = {}) {
  const url = `${BASE_URL}${endpoint}`;

  // Saada Content-Type ainult siis kui body on olemas — vastasel juhul
  // Fastify keeldub FST_ERR_CTP_EMPTY_JSON_BODY veaga.
  const hasBody = options.body !== undefined && options.body !== null;
  const config = {
    headers: hasBody
      ? { 'Content-Type': 'application/json', ...options.headers }
      : { ...options.headers },
    ...options,
  };

  if (hasBody && typeof options.body === 'object') {
    config.body = JSON.stringify(options.body);
  }

  const response = await fetch(url, config);

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(error.message || error.error || `HTTP ${response.status}`);
  }

  return response.json();
}

export function useApi() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const request = useCallback(async (endpoint, options = {}) => {
    setLoading(true);
    setError(null);

    try {
      const data = await fetchApi(endpoint, options);
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const get = useCallback((endpoint) => request(endpoint), [request]);

  const post = useCallback((endpoint, body) =>
    request(endpoint, { method: 'POST', body }), [request]);

  const put = useCallback((endpoint, body) =>
    request(endpoint, { method: 'PUT', body }), [request]);

  return { get, post, put, loading, error };
}

// API functions
export const api = {
  // Dashboard
  getDashboard: () => fetchApi('/dashboard'),
  getDashboardSummary: () => fetchApi('/dashboard/summary'),
  getChartData: (period) => fetchApi(`/dashboard/chart/${period}`),

  // Devices
  getDevices: () => fetchApi('/devices'),
  getDevice: (id) => fetchApi(`/devices/${id}`),
  testDevice: (id) => fetchApi(`/devices/${id}/test`, { method: 'POST' }),
  getModbusData: () => fetchApi('/devices/modbus/all'),

  // Alarms
  getAlarms: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return fetchApi(`/alarms${query ? `?${query}` : ''}`);
  },
  getActiveAlarms: () => fetchApi('/alarms/active'),
  acknowledgeAlarm: (id) => fetchApi(`/alarms/${id}/acknowledge`, { method: 'POST' }),
  acknowledgeAllAlarms: () => fetchApi('/alarms/acknowledge-all', { method: 'POST' }),
  getAlarmDefinitions: () => fetchApi('/alarms/definitions'),
  getAlarmStatistics: (period) => fetchApi(`/alarms/statistics?period=${period}`),

  // Control — kasutab valitud sait-i (SiteSelector localStorage). Kui slot
  // valitud, kõned lähevad `/api/sites/:slot/control/*`. Kui mitte, vana
  // single-site endpoint (backward compat main-slot jaoks).
  setPower: (power) => fetchApi(_ctrlPath('power'), { method: 'POST', body: { power } }),
  startPCS: () => fetchApi(_ctrlPath('start'), { method: 'POST' }),
  stopPCS: () => fetchApi(_ctrlPath('stop'), { method: 'POST' }),
  emergencyStop: () => fetchApi(_ctrlPath('emergency'), { method: 'POST' }),
  setMode: (mode) => fetchApi('/control/mode', { method: 'POST', body: { mode } }),  // operating mode (price/peak/...) — globaalne
  resetAlarms: () => fetchApi(_ctrlPath('reset'), { method: 'POST' }),
  gridControl: (action) => fetchApi('/control/grid', { method: 'POST', body: { action } }),  // BMS-side, jääb global
  getControlStatus: () => fetchApi('/control/status'),
  setRemoteControl: (enabled) => fetchApi(_ctrlPath('remote'), { method: 'POST', body: { enabled } }),
  setGridMode: (mode) => fetchApi(_ctrlPath('grid-mode'), { method: 'POST', body: { mode } }),
  setPowerFactor: (pf) => fetchApi(_ctrlPath('power-factor'), { method: 'POST', body: { pf } }),
  reloadSites: () => fetchApi('/sites/reload', { method: 'POST' }),

  // Liquid Cooling
  getCooling: () => fetchApi('/control/cooling'),
  setCoolingEnabled: (enabled) => fetchApi('/control/cooling/enable', { method: 'POST', body: { enabled } }),
  setCoolingMode: (mode) => fetchApi('/control/cooling/mode', { method: 'POST', body: { mode } }),
  setCoolingSetpoint: (cooling, heating) => fetchApi('/control/cooling/setpoint', {
    method: 'POST',
    body: { cooling, heating }
  }),

  // Safety Sensors
  getSafetySensors: () => fetchApi('/control/safety-sensors'),

  // Config
  getConfig: () => fetchApi('/config'),
  updateConfig: (config) => fetchApi('/config', { method: 'PUT', body: config }),
  getPrices: () => fetchApi('/config/prices'),
  exportConfig: () => fetchApi('/config/export', { method: 'POST' }),
  importConfig: (config) => fetchApi('/config/import', { method: 'POST', body: config }),
  resetConfig: () => fetchApi('/config/reset', { method: 'POST', body: { confirm: 'RESET' } }),

  // Schedule (15-minute slots)
  getSchedules: () => fetchApi('/schedule'),
  getActiveSchedule: () => fetchApi('/schedule/active'),
  getSchedulePreview: () => fetchApi('/schedule/preview'),
  getScheduleStatistics: () => fetchApi('/schedule/statistics'),
  updateSlot: (index, settings) => fetchApi(`/schedule/slot/${index}`, { method: 'PUT', body: settings }),
  updateSlots: (slots) => fetchApi('/schedule/slots', { method: 'PUT', body: { slots } }),
  updateSlotRange: (startIndex, endIndex, settings) => fetchApi('/schedule/slots/range', {
    method: 'PUT',
    body: { startIndex, endIndex, ...settings }
  }),
  applyTemplate: (template) => fetchApi('/schedule/template', { method: 'POST', body: { template } }),
  enableScheduler: (enabled) => fetchApi('/schedule/enable', { method: 'POST', body: { enabled } }),
  resetSchedules: () => fetchApi('/schedule/reset', { method: 'POST', body: { confirm: 'RESET' } }),

  // Schedule (legacy)
  addSchedule: (schedule) => fetchApi('/schedule', { method: 'POST', body: schedule }),
  updateSchedule: (id, schedule) => fetchApi(`/schedule/${id}`, { method: 'PUT', body: schedule }),
  deleteSchedule: (id) => fetchApi(`/schedule/${id}`, { method: 'DELETE' }),
};

export default useApi;
